import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, X, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function UploadZone({ onFileUpload, onClose }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = async (files) => {
    const pdfFile = files.find((file) => file.type === "application/pdf");

    if (!pdfFile) {
      setError("Please upload a PDF file");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Normalize the filename to have a lowercase extension
      const originalName = pdfFile.name;
      const extensionIndex = originalName.lastIndexOf(".");
      const nameWithoutExt =
        extensionIndex === -1
          ? originalName
          : originalName.substring(0, extensionIndex);
      const extension =
        extensionIndex === -1
          ? ""
          : originalName.substring(extensionIndex).toLowerCase();
      const newName = `${nameWithoutExt}${extension}`;

      const normalizedFile = new File([pdfFile], newName, {
        type: pdfFile.type,
        lastModified: pdfFile.lastModified,
      });

      await onFileUpload(normalizedFile);
    } catch (error) {
      setError("Error processing file. Please try again.");
      console.error(error); // Add console.error as per outline
    }

    setUploading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Upload Blood Test PDF
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 ${
              dragActive
                ? "border-blue-400 bg-blue-50"
                : "border-slate-300 hover:border-slate-400"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileInput}
              className="hidden"
            />

            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="w-12 h-12 mx-auto text-blue-600 animate-spin" />
                <p className="text-slate-600">
                  Processing your blood test PDF...
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Drop your PDF here
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Upload blood test results in PDF format for automatic
                    processing
                  </p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Browse Files
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Supported format: PDF only
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
